import { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Zoom } from '@mui/material';
import { ChevronUp, ChevronDown } from 'lucide-react';

export function ScrollNavigationControls({ data = {} }) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const primaryColor = data.primary_color || '#1e3a8a';

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;

      if (totalScroll <= 0) {
        setIsVisible(false);
        return;
      }

      setIsVisible(true);
      const progress = Math.min(100, Math.max(0, (currentScroll / totalScroll) * 100));
      setScrollProgress(progress);

      setIsAtTop(currentScroll <= 80);
      setIsAtBottom(currentScroll >= totalScroll - 80);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  };

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 20, md: 28 },
        right: { xs: 20, md: 28 },
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          borderRadius: 50,
          p: 0.6,
          boxShadow: '0 10px 30px -4px rgba(0, 0, 0, 0.15), 0 4px 12px -2px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 14px 36px -4px rgba(0, 0, 0, 0.2), 0 6px 16px -2px rgba(0, 0, 0, 0.1)',
            borderColor: `${primaryColor}40`,
          },
        }}
      >
        {/* Scroll Up Button - Visible when not at top */}
        <Zoom in={!isAtTop} unmountOnExit>
          <Tooltip title={`Scroll to Top (${Math.round(scrollProgress)}%)`} placement="left" arrow>
            <IconButton
              onClick={scrollToTop}
              aria-label="Scroll to top"
              size="small"
              sx={{
                width: 40,
                height: 40,
                color: primaryColor,
                backgroundColor: 'transparent',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  color: '#ffffff',
                  transform: 'translateY(-2px) scale(1.08)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <ChevronUp size={22} strokeWidth={2.4} />
            </IconButton>
          </Tooltip>
        </Zoom>

        {/* Scroll Progress Ring / Divider */}
        {!isAtTop && !isAtBottom && (
          <Box
            sx={{
              width: 24,
              height: 2,
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              borderRadius: 1,
              my: 0.2,
            }}
          />
        )}

        {/* Scroll Down Button - Visible when not at bottom */}
        <Zoom in={!isAtBottom} unmountOnExit>
          <Tooltip title="Scroll to Bottom" placement="left" arrow>
            <IconButton
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              size="small"
              sx={{
                width: 40,
                height: 40,
                color: isAtTop ? '#ffffff' : primaryColor,
                backgroundColor: isAtTop ? primaryColor : 'transparent',
                borderRadius: '50%',
                boxShadow: isAtTop ? `0 4px 14px ${primaryColor}50` : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  color: '#ffffff',
                  transform: 'translateY(2px) scale(1.08)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <ChevronDown size={22} strokeWidth={2.4} />
            </IconButton>
          </Tooltip>
        </Zoom>
      </Box>
    </Box>
  );
}

export default ScrollNavigationControls;
